// The drawer's Account section (#323): sign in with a magic link, activate
// with an invite code, sign out. Renders NOTHING when auth is unconfigured —
// the Pages-row lesson (disabled beats hidden) applies to options on a
// surface, not to a feature that has not launched: a dead sign-in form in
// today's deployed app would only invite questions it cannot answer.
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { appleClientId, googleClientId } from '@/auth/config';
import { prepareAppleSignIn, type AppleSignIn } from '@/auth/appleSignIn';
import { initGoogleIdentity } from '@/auth/googleButton';
import { accountLabel } from '@/auth/identity';
import {
  redeemCode,
  resetToSignedOut,
  sendMagicLink,
  signInWithGoogle,
  signInWithIdToken,
  signOut,
  useAuth,
} from '@/auth/session';
import { uploadDeviceTunes, useAccountSyncStatus } from '@/data/accountSync';

/** Google's "G", inline so the button needs no asset fetch. The four-colour
 * mark is Google's own brand requirement for sign-in buttons — a currentColor
 * glyph would break their guideline and read as generic. */
function GoogleMark() {
  return (
    <svg width="14" height="14" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

/** Continue with Google. With a Google client id in the build this is
 * Google's own button, which signs in on this site; until it loads, if it
 * cannot, or with no client id, it is our button and the redirect flow. */
function GoogleSignIn({ disabled }: { disabled: boolean }) {
  const clientId = googleClientId();
  const host = useRef<HTMLDivElement>(null);
  const [drawn, setDrawn] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    void (async () => {
      try {
        const api = await initGoogleIdentity(clientId, (token, nonce) => {
          void signInWithIdToken('google', token, nonce);
        });
        const parent = host.current;
        if (cancelled || !parent) return;
        api.renderButton(parent, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          // Google's button is an iframe of a fixed width (≤400): match the
          // column the Apple button and the email form fill.
          width: Math.min(400, parent.clientWidth || 240),
        });
        setDrawn(true);
      } catch {
        // Offline or blocked: our button and the redirect stay.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  return (
    <>
      {clientId && (
        <div
          ref={host}
          className="account-google-gis"
          // Laid out (empty, no height) before it is drawn, never hidden: the
          // button is sized from this box's width, and a hidden box is 0 wide.
          data-drawn={drawn || undefined}
          data-testid="account-google-gis"
        />
      )}
      {!drawn && (
        <button
          type="button"
          className="btn btn-sm account-google"
          onClick={() => void signInWithGoogle()}
          disabled={disabled}
          data-testid="account-google"
        >
          <GoogleMark /> Continue with Google
        </button>
      )}
    </>
  );
}

/** Apple's mark, inline and in the button's own ink (Apple's guideline:
 * white on black or black on white, never a colour). */
function AppleMark() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
    </svg>
  );
}

/** Continue with Apple, shown only with an Apple Services ID in the build.
 * Apple's script is prepared on mount so the click can open the popup at
 * once (a popup opened after an await is blocked); until it is ready, or if
 * it cannot load, the button stays disabled. */
function AppleSignInButton({ disabled }: { disabled: boolean }) {
  const clientId = appleClientId();
  const [apple, setApple] = useState<AppleSignIn | null>(null);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    prepareAppleSignIn(clientId).then(
      (ready) => {
        if (!cancelled) setApple(ready);
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  if (!clientId) return null;
  return (
    <button
      type="button"
      className="btn btn-sm account-apple"
      onClick={() => {
        if (!apple) return;
        void apple.signIn().then((result) => {
          if (result) void signInWithIdToken('apple', result.token, result.nonce, result.name);
        });
      }}
      disabled={disabled || !apple}
      data-testid="account-apple"
    >
      <AppleMark /> Continue with Apple
    </button>
  );
}

/** Where your saved tunes, tune settings, history and loves are: in your
 * account, or some tunes still on this device only, which go up when you say
 * so. */
function AccountTunes() {
  const sync = useAccountSyncStatus();
  if (sync.phase === 'idle') return null;
  const tunes = (n: number) => `${n} ${n === 1 ? 'tune is' : 'tunes are'}`;
  return (
    <div className="account-sync" data-testid="account-sync" data-phase={sync.phase}>
      {sync.phase === 'error' ? (
        <p className="settings-note account-error" role="alert" data-testid="account-sync-status">
          Not synced: {sync.error}
        </p>
      ) : sync.deviceOnly > 0 ? (
        <>
          <p className="settings-note" data-testid="account-sync-status">
            {tunes(sync.deviceOnly)} on this device only.
          </p>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => void uploadDeviceTunes()}
            disabled={sync.phase === 'syncing'}
            data-testid="account-upload"
          >
            Add to my account
          </button>
        </>
      ) : (
        <p className="settings-note" data-testid="account-sync-status">
          {sync.phase === 'syncing'
            ? 'Syncing'
            : 'Your tunes, settings, history and loves are in your account.'}
        </p>
      )}
    </div>
  );
}

export function AccountSection() {
  const auth = useAuth();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');

  if (auth.status === 'unconfigured') return null;

  const submitEmail = (event: FormEvent) => {
    event.preventDefault();
    void sendMagicLink(email);
  };
  const submitCode = (event: FormEvent) => {
    event.preventDefault();
    void redeemCode(code);
  };

  return (
    <section className="settings-section" aria-labelledby="account-settings-heading">
      <h3 id="account-settings-heading" className="settings-section-title">
        Account
      </h3>

      {(auth.status === 'signedOut' || auth.status === 'loading') && (
        <>
          <GoogleSignIn disabled={auth.status === 'loading' || auth.busy} />
          <AppleSignInButton disabled={auth.status === 'loading' || auth.busy} />
          <form className="account-form" onSubmit={submitEmail}>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="Email"
              aria-label="Email"
              autoComplete="email"
              disabled={auth.status === 'loading' || auth.busy}
              data-testid="account-email"
            />
            <button
              type="submit"
              className="btn btn-sm btn-ghost"
              disabled={auth.status === 'loading' || auth.busy || email.trim() === ''}
              data-testid="account-send-link"
            >
              Send sign-in link
            </button>
          </form>
        </>
      )}

      {auth.status === 'linkSent' && (
        <>
          <p className="settings-note" data-testid="account-status">
            Sign-in link sent to {auth.email}. Open it on this device.
          </p>
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={resetToSignedOut}
            data-testid="account-restart"
          >
            Use a different email
          </button>
        </>
      )}

      {auth.status === 'needsCode' && (
        <>
          <p className="settings-note" data-testid="account-status">
            Signed in as {accountLabel(auth.email, auth.name)}. Enter your invite code to join the
            pilot.
          </p>
          <form className="account-form" onSubmit={submitCode}>
            <input
              type="text"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              placeholder="Invite code"
              aria-label="Invite code"
              autoComplete="off"
              autoCapitalize="none"
              disabled={auth.busy}
              data-testid="account-code"
            />
            <button
              type="submit"
              className="btn btn-sm btn-ghost"
              disabled={auth.busy || code.trim() === ''}
              data-testid="account-activate"
            >
              Activate
            </button>
          </form>
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => void signOut()}
            disabled={auth.busy}
            data-testid="account-signout"
          >
            Sign out
          </button>
        </>
      )}

      {auth.status === 'active' && (
        <>
          <p className="settings-note" data-testid="account-status">
            Signed in as {accountLabel(auth.email, auth.name)}.
          </p>
          <AccountTunes />
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => void signOut()}
            disabled={auth.busy}
            data-testid="account-signout"
          >
            Sign out
          </button>
        </>
      )}

      {auth.error && (
        <p className="settings-note account-error" role="alert" data-testid="account-error">
          {auth.error}
        </p>
      )}
    </section>
  );
}
